import { onboarding } from '@cwos/api-client';
import { httpStatusOf } from '../LoadFailure.js';

/**
 * Pure view logic for Settings › Getting set up (§O9).
 *
 * ONE RULE GOVERNS EVERY FUNCTION HERE: the server's answer is the account
 * state, and nothing in this module computes a completion, a percentage or a
 * "you're all set" from anything else. A wizard that decides it is finished
 * because somebody pressed fourteen buttons is exactly the claim that is wrong
 * the one time a write silently failed, and it is wrong in the direction that
 * sends a customer live on a half-configured tenant.
 *
 * So `readProgress` reads `percentComplete`, `completedSteps` and `isLive`
 * straight off `GET /api/onboarding/status`, and the row states below are
 * derived from each item's own `completedAt` — the timestamp the database
 * wrote — never from a mutation's return value and never from local state.
 */

export type StepKey = onboarding.OnboardingStep;

/**
 * HOW THE SERVER DECIDES A STEP IS TRUE, because "mark complete" means four
 * different things across the fourteen and a person is entitled to know which
 * one they are looking at.
 *
 * Read off `apps/api/src/domains/onboarding/service.ts`'s `STEP_VALIDATORS`,
 * which is the only thing that decides:
 *
 *   `automatic`  ORGANIZATION_CREATED. Derived by the server from the
 *                organization's own existence and refused (422
 *                `STEP_AUTO_COMPLETED`) to every manual attempt.
 *   `checked`    The validator counts rows in a table that always exists, so
 *                the step cannot be marked while the condition is false.
 *   `checked-when-connected`  The validator counts rows in a table that may not
 *                be deployed, and returns `{ valid: true }` when it is not. So
 *                a completion here is a check on some deployments and an
 *                attestation on others, and this surface must not call it
 *                "verified" on either.
 *   `attested`   SIMULATION_COMPLETE and LIVE are `ALWAYS_VALID`. Marking them
 *                records that somebody said so. It checks nothing.
 *
 * A mirror of server behaviour can drift, and this one is guarded rather than
 * trusted: `stepGuide` is total over `onboarding.ONBOARDING_STEPS` and the unit
 * suite asserts the two lists are identical, so a fifteenth step added on the
 * server fails the build here instead of rendering as a nameless row.
 */
export type StepProof = 'automatic' | 'checked' | 'checked-when-connected' | 'attested';

export interface StepGuide {
  step: StepKey;
  /** The step in a person's words, not the enum's. */
  label: string;
  /** What has to be true, said as the condition rather than as an instruction. */
  meaning: string;
  /**
   * Where the work is actually done. Absent where the admin app genuinely has
   * no screen for it — an invented href is a 404 with a customer behind it, and
   * `CLIENTS_REVIEWED` is the honest example: clients arrive through the
   * records integration and there is no client directory to point at.
   */
  href?: string;
  proof: StepProof;
}

const GUIDES: Record<StepKey, Omit<StepGuide, 'step'>> = {
  ORGANIZATION_CREATED: {
    label: 'Organization created',
    meaning: 'Your organization exists in Phibian. Recorded by sign-up itself.',
    href: '/settings/org',
    proof: 'automatic',
  },
  ADMIN_INVITED: {
    label: 'First administrator signed in',
    meaning: 'At least one administrator has accepted their invitation and is active.',
    href: '/settings/people',
    proof: 'checked',
  },
  LOCATIONS_CONFIGURED: {
    label: 'Sites added',
    meaning: 'At least one site exists — the places your teams work from.',
    href: '/settings/sites',
    proof: 'checked',
  },
  PROGRAMS_CONFIGURED: {
    label: 'Programs added',
    meaning: 'At least one program exists — the operational units work belongs to.',
    href: '/settings/programs',
    proof: 'checked',
  },
  EHR_CONNECTED: {
    label: 'Records system connected',
    meaning: 'A records integration is connected or syncing.',
    href: '/settings/records',
    proof: 'checked-when-connected',
  },
  INITIAL_SYNC_COMPLETE: {
    label: 'First import finished',
    meaning: 'The first import from your records system brought records across.',
    href: '/settings/records',
    proof: 'checked-when-connected',
  },
  WORKERS_REVIEWED: {
    label: 'Staff reviewed',
    meaning: 'At least one worker record exists and has been looked over.',
    href: '/people',
    proof: 'checked',
  },
  CLIENTS_REVIEWED: {
    label: 'Clients reviewed',
    meaning: 'At least one client record exists and has been looked over.',
    proof: 'checked',
  },
  HOURS_CONFIGURED: {
    label: 'Expected hours set',
    meaning: 'At least one worker has expected weekly hours, so gaps can be noticed.',
    href: '/people',
    proof: 'checked',
  },
  RULES_CONFIGURED: {
    label: 'Automations turned on',
    meaning: 'At least one automation is enabled and watching your data.',
    href: '/settings/rules',
    proof: 'checked-when-connected',
  },
  POLICIES_CONFIGURED: {
    label: 'Location and time policy set',
    meaning: 'At least one location policy is configured, so clock-in rules apply.',
    href: '/settings/location',
    proof: 'checked',
  },
  WORKERS_INVITED: {
    label: 'Staff invited',
    meaning: 'At least one worker has been invited to sign in.',
    href: '/settings/people',
    proof: 'checked',
  },
  SIMULATION_COMPLETE: {
    label: 'Trial run done',
    meaning: 'You have run a day through Phibian before trusting it with a real one.',
    proof: 'attested',
  },
  LIVE: {
    label: 'Live',
    meaning: 'You are running your operation on Phibian.',
    proof: 'attested',
  },
};

/**
 * The guide for a step key, or `undefined` for a key this build does not know.
 *
 * `undefined` rather than a fabricated label, and the screen renders the raw key
 * beside a plain note instead of dropping the row. Same reasoning as
 * `roles-permissions/catalog.ts`: a key the server sent and this client has no
 * copy for is a fact about the organization, and hiding it would make this
 * screen a second, quietly wrong source of what setup involves.
 */
export function stepGuide(step: string): StepGuide | undefined {
  const guide = GUIDES[step as StepKey];
  return guide ? { step: step as StepKey, ...guide } : undefined;
}

/** Every guide this build carries, in the api-client's sequence order. */
export function allStepGuides(): StepGuide[] {
  return onboarding.ONBOARDING_STEPS.map((step) => ({ step, ...GUIDES[step] }));
}

// ---------------------------------------------------------------------------
// Where the sequence stands
// ---------------------------------------------------------------------------

/**
 * WHAT THIS ORGANIZATION CAN DO NEXT — and the third answer, which is the one
 * this repository actually produces today.
 *
 *   `live`         the server says `isLive`. Its word, not a count of rows.
 *   `in-progress`  `next` is a step `POST /complete/:step` will consider.
 *   `unstartable`  the outstanding step is `ORGANIZATION_CREATED`, which the
 *                  service refuses to advance by hand (422 `STEP_AUTO_COMPLETED`).
 *
 * THE THIRD IS NOW A SERVER FAULT, NOT THE ORDINARY CASE, and the change is
 * worth stating because this branch was written when it was the ordinary case.
 * `POST /api/onboarding/signup` answers 404 unless the deployment sets
 * `PUBLIC_SIGNUP_ENABLED`, which production deliberately does not
 * (docs/PRODUCTION-BLOCKER-public-tenant-creation.md; ADR 0015 keeps tenant
 * provisioning a control-plane action). Every organization reaches Phibian
 * through `tenancy.createOrganization`, which writes no checklist row — so for
 * a long time every real tenant's checklist was empty, its `currentStep` was
 * `ORGANIZATION_CREATED`, and the sequential guard refused all thirteen
 * remaining steps behind a step nothing could complete.
 *
 * The server now DERIVES that first step from the organization's own existence
 * (`onboarding/service.ts loadChecklist`), so a tenant whose organization row is
 * visible reports it complete however the organization was created. The branch
 * stays because a client must not assume the server it is talking to does that:
 * an older API, or one that genuinely cannot see the row, still answers this
 * shape, and a wizard that offered a "Mark complete" button for it would hand a
 * customer a control whose only possible outcome is a refusal.
 */
export type SequenceState =
  { kind: 'live' } | { kind: 'in-progress'; next: StepKey } | { kind: 'unstartable' };

export function sequenceState(status: onboarding.OnboardingStatus): SequenceState {
  if (status.isLive) return { kind: 'live' };
  const outstanding = status.checklist.find((item) => item.completedAt === null);
  // No outstanding item and `isLive` false: the server's own two fields
  // disagree. Trust the checklist rows — they are what `completeStep` reads —
  // and treat the sequence as still needing LIVE.
  if (!outstanding) return { kind: 'in-progress', next: 'LIVE' };
  if (outstanding.step === 'ORGANIZATION_CREATED') return { kind: 'unstartable' };
  return { kind: 'in-progress', next: outstanding.step };
}

export interface Progress {
  /** Steps the server lists as complete. */
  done: number;
  /** Steps the server sent, not a constant — a longer sequence reports longer. */
  total: number;
  /** The server's own rounded figure. Never recomputed here. */
  percent: number;
  /** The most recent completion instant on the checklist, or null. */
  lastCompletedAt: string | null;
  /** The step that instant belongs to, or null. */
  lastCompletedStep: StepKey | null;
}

/**
 * The resume anchor.
 *
 * `lastCompletedAt` is what makes a return visit legible: somebody who closed
 * the tab a week ago needs to know that the work they did is still recorded,
 * and the only evidence of that which is not a guess is the timestamp the
 * database wrote. It is a max over the checklist rather than a "last thing I
 * clicked", so it survives a different browser, a different administrator and a
 * session that ended mid-step.
 */
export function readProgress(status: onboarding.OnboardingStatus): Progress {
  let lastCompletedAt: string | null = null;
  let lastCompletedStep: StepKey | null = null;
  for (const item of status.checklist) {
    if (item.completedAt === null) continue;
    if (lastCompletedAt === null || item.completedAt > lastCompletedAt) {
      lastCompletedAt = item.completedAt;
      lastCompletedStep = item.step;
    }
  }
  return {
    done: status.completedSteps.length,
    total: status.checklist.length,
    percent: status.percentComplete,
    lastCompletedAt,
    lastCompletedStep,
  };
}

// ---------------------------------------------------------------------------
// What happened to a write
// ---------------------------------------------------------------------------

/**
 * THE FIVE ENDINGS OF "MARK COMPLETE", and why a single "couldn't save" would
 * be wrong for four of them.
 *
 *   `refused`      403. The account may not advance steps. An answer; no retry.
 *   `not-yet`      422. The server LOOKED and the condition is not true — no
 *                  programs exist, no worker has hours. The message names the
 *                  condition, so it is shown verbatim. Trying again without
 *                  doing the work repeats it; doing the work and trying again
 *                  succeeds, so this one does keep a retry.
 *   `signed-out`   401. Neither a permission problem nor a data problem.
 *   `failed`       anything else — a 500, a proxy, a dropped connection. The
 *                  only ending for which "try again" alone is honest advice.
 *   `unconfirmed`  the request was ACCEPTED and the checklist re-read afterwards
 *                  still does not carry the step. See `writeOutcome`.
 *
 * In every one of the five the step is NOT complete, and the screen says so in
 * the same words each time, because the failure this design exists to prevent
 * is a row that looks done because a button was pressed.
 */
export type WriteOutcome =
  /** `error` rides along so the refusal can name the permission the server named. */
  | { kind: 'refused'; error: unknown }
  | { kind: 'not-yet'; message: string }
  | { kind: 'signed-out' }
  | { kind: 'failed' }
  | { kind: 'unconfirmed' };

/**
 * Classify a rejected `completeStep`.
 *
 * Structural on `status`, like `LoadFailure.httpStatusOf`, rather than
 * `instanceof ApiError`: this screen is mounted inside an app with two
 * unrelated error shapes and a refusal misread as a fault is a retry button
 * over an answer.
 */
export function writeOutcomeOfError(err: unknown): WriteOutcome {
  const status = httpStatusOf(err);
  if (status === 403) return { kind: 'refused', error: err };
  if (status === 401) return { kind: 'signed-out' };
  if (status === 422) {
    const message =
      typeof err === 'object' && err !== null && typeof (err as Error).message === 'string'
        ? (err as Error).message
        : '';
    return {
      kind: 'not-yet',
      // The server always sends one; the fallback exists so a stripped body
      // cannot render an empty sentence where a reason belongs.
      message:
        message.trim() === ''
          ? 'The server refused this step and did not say why. Nothing has been recorded.'
          : message,
    };
  }
  return { kind: 'failed' };
}

/**
 * What to show for a step whose write has finished, given the checklist that
 * came back AFTER it.
 *
 * THIS IS THE POINT OF THE WHOLE MODULE. `completeStep` returns the completed
 * item, and a screen that painted the row from that return value would be
 * reporting the client's hope rather than the tenant's state. So the row is
 * painted from the re-read status, and a success whose re-read does not carry
 * the step becomes `unconfirmed` — visible, named, and not a checkmark.
 *
 * `undefined` means there is nothing to say: either no attempt has been made,
 * or the attempt succeeded and the server's own checklist agrees.
 */
export function writeOutcome(
  attempt: { settled: boolean; error: unknown } | undefined,
  completedAfter: boolean,
): WriteOutcome | undefined {
  if (!attempt || !attempt.settled) return undefined;
  if (attempt.error !== undefined && attempt.error !== null) {
    return writeOutcomeOfError(attempt.error);
  }
  return completedAfter ? undefined : { kind: 'unconfirmed' };
}
